<template>
  <div class="h-screen flex flex-col md:flex-row">
    <!-- Left side -->
    <div class="bg-gray-50 flex flex-col justify-center items-center w-full md:w-1/2 p-10">
      <!-- Login form -->
      <form v-if="isLoginForm" @submit.prevent="handleLogin" class="w-full max-w-md">
        <h2 class="text-center text-lg font-semibold mb-8">
          Êtes-vous déjà client ?
        </h2>
        <label class="block text-sm font-semibold mb-1" for="email">
          Email *
        </label>
        <input v-model="loginData.email" class="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400" id="email" placeholder="Email" required type="email"/>
        <label class="block text-sm font-semibold mb-1" for="password">
          Mot de passe *
        </label>
        <div class="relative">
          <input v-model="loginData.password" :type="passwordFieldType" class="w-full mb-2 px-4 py-2 border border-gray-300 rounded-md text-gray-400 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400" id="password" placeholder="Mot de passe" required/>
          <span @click="togglePasswordVisibility" class="absolute inset-y-0 right-3 flex items-center text-gray-500 cursor-pointer">
            <i :class="passwordIconClass"></i>
          </span>
        </div>
        <a class="block mb-6 text-sm font-semibold underline text-black" href="#" @click.prevent="showResetPasswordForm">
          Mot de passe oublié ?
        </a>
        <button class="w-full bg-blue-900 text-white py-3 rounded-md mb-6 hover:bg-gray-900 transition-colors" type="submit">
          Se connecter
        </button>
        <div class="flex items-center mb-6">
          <hr class="flex-grow border-gray-300"/>
          <span class="mx-3 text-xs font-semibold text-gray-600">
            OU
          </span>
          <hr class="flex-grow border-gray-300"/>
        </div>
        <h3 class="text-center text-lg font-semibold mb-4">
          Nouveau Client ?
        </h3>
        <button class="w-full border border-gray-700 rounded-md py-3 text-sm hover:bg-gray-200 transition-colors" type="button" @click="showRegisterForm">
          Créer mon compte
        </button>
      </form>

      <!-- Reset Password form -->
      <form v-if="isResetPasswordForm" @submit.prevent="handleResetPassword" class="w-full max-w-md">
        <h2 class="text-center text-lg font-semibold mb-8">
          Réinitialiser le mot de passe
        </h2>
        <label class="block text-sm font-semibold mb-1" for="email-reset">
          Email *
        </label>
        <input v-model="resetPasswordData.email" class="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400" id="email-reset" placeholder="Email" required type="email"/>
        <button class="w-full bg-blue-900 text-white py-3 rounded-md mb-6 hover:bg-gray-900 transition-colors" type="submit">
          Envoyer le lien de réinitialisation
        </button>
        <button class="w-full border border-gray-700 rounded-md py-3 text-sm hover:bg-gray-200 transition-colors" type="button" @click="showLoginForm">
          Retour à la connexion
        </button>
      </form>

      <!-- Register form -->
      <form v-else-if="!isLoginForm && !isResetPasswordForm" @submit.prevent="handleRegister" class="w-full mx-auto" >
    <!-- Panneau principal -->
    <div class="flex-1 flex flex-col" >
      <!-- Barre de progression -->
      <div class="flex items-center justify-between w-full mb-16 px-2 md:px-8">
        <template v-for="(step, index) in steps" :key="index">
          <div class="flex items-center w-full relative">
            <!-- Point -->
            <div class="flex flex-col items-center z-10">
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                :class="{
                  'bg-[#3f44d6] text-white': currentStep > index,
                  'border-2 border-[#3f44d6] text-[#3f44d6]': currentStep === index,
                  'border border-[#d6d6d6] text-[#d6d6d6]': currentStep < index,
                }"
              >
                <i class="fas fa-circle"></i>
              </div>
              <span
                class="mt-2 text-xs font-semibold text-center"
                :class="{
                  'text-[#1a1a1a]': currentStep >= index,
                  'text-[#a3a3a3]': currentStep < index,
                }"
              >
                {{ step }}
              </span>
            </div>

            <!-- Ligne -->
            <div
              v-if="index < steps.length - 1"
              class="flex-grow h-[2px]"
              :class="currentStep > index ? 'bg-[#3f44d6]' : 'bg-[#d6d6d6]'"
            ></div>
          </div>
        </template>
      </div>

      <!-- Étape 1 : Choix du type d'organisation -->
      <div v-if="currentStep === 0" class="max-w-2xl mx-auto text-center">
        <h2 class="text-2xl font-bold mb-6">Quel est votre type d'organisation ?</h2>
        <div class="grid grid-cols-2 gap-6">
          <button @click="selectedOption = 'solo'" :class="[baseBtnClass, selectedOption === 'solo' ? activeBtnClass : '']">
            <i class="fas fa-user text-2xl mb-2"></i>
            <span>Je suis seul(e)</span>
          </button>
          <button @click="selectedOption = 'equipe'" :class="[baseBtnClass, selectedOption === 'equipe' ? activeBtnClass : '']">
            <i class="fas fa-users text-2xl mb-2"></i>
            <span>J'ai une équipe</span>
          </button>
        </div>
      </div>

      <!-- Étape 2 : Informations personnelles -->
      <div v-if="currentStep === 1" class="max-w-2xl mx-auto">
        <h2 class="text-2xl font-bold mb-6">Vos informations personnelles</h2>
        <!-- Champs pour les informations personnelles -->
        <div class="mb-5">
          <label for="nom" class="block mb-2 text-sm font-medium text-gray-900 ">Nom</label>
          <input type="text" id="nom" v-model="nom" placeholder="Dupont" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
        <div class="mb-5">
          <label for="prenom" class="block mb-2 text-sm font-medium text-gray-900 ">Prénom</label>
          <input type="text" id="prenom" v-model="prenom" placeholder="Marie" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
        <div class="mb-5">
          <label for="telephone" class="block mb-2 text-sm font-medium text-gray-900 ">Téléphone</label>
          <input type="tel" id="telephone" v-model="telephone" placeholder="+33 6 12 34 56 78" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
        <div class="mb-5">
          <label for="email" class="block mb-2 text-sm font-medium text-gray-900 ">Email</label>
          <input type="email" id="email" v-model="email" placeholder="name@flowbite.com" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
        <div class="mb-5">
          <label for="password" class="block mb-2 text-sm font-medium text-gray-900 ">Mot de passe</label>
          <input type="password" id="password" v-model="password" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
      </div>

      <!-- Étape 3 : Informations entreprise -->
      <div v-if="currentStep === 2" class="max-w-2xl mx-auto">
        <h2 class="text-2xl font-bold mb-6">Informations sur votre entreprise</h2>
        <!-- Champs pour les informations de l'entreprise -->
        <div class="mb-5">
          <label for="entreprise" class="block mb-2 text-sm font-medium text-gray-900 ">Nom de l'entreprise</label>
          <input type="text" id="entreprise" v-model="entreprise" placeholder="Salon Beauté Naturelle" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
        <div class="mb-5">
          <label for="tel_entreprise" class="block mb-2 text-sm font-medium text-gray-900 ">Téléphone de l'entreprise</label>
          <input type="tel" id="tel_entreprise" v-model="telEntreprise" placeholder="+33 1 23 45 67 89" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
        <div class="mb-5">
          <label for="siret" class="block mb-2 text-sm font-medium text-gray-900 ">Numéro SIRET</label>
          <input type="text" id="siret" v-model="siret" placeholder="123 456 789 00012" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
        <div class="mb-5">
          <label for="adresse" class="block mb-2 text-sm font-medium text-gray-900 ">Adresse de l'entreprise</label>
          <input type="text" id="adresse" v-model="adresse" placeholder="12 rue des Lilas, 75000 Paris" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" required />
        </div>
      </div>

      <!-- Étape 4 : Tarifs abonnement -->
      <div v-if="currentStep === 3" class="max-w-2xl mx-auto">
        <h2 class="text-2xl font-bold mb-6">Choisissez votre formule d’abonnement</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <!-- Option flexible -->
          <div class="border rounded-lg p-4 hover:shadow-lg transition cursor-pointer" @click="selectedTarif = 'flexible'; montantFlexible = ''" :class="selectedTarif === 'flexible' ? 'border-[#3f44d6] bg-gray-50' : ''">
            <h3 class="font-bold text-lg mb-2">Flexible</h3>
            <p>Payez ce que vous voulez</p>
            <div v-if="selectedTarif === 'flexible'" class="mt-4">
              <label for="montantFlexible" class="block mb-2 text-sm font-medium text-gray-900">Montant mensuel (€)</label>
              <input v-model="montantFlexible" type="number" id="montantFlexible" placeholder="Ex : 20" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>

          <!-- Option classique -->
          <div class="border rounded-lg p-4 hover:shadow-lg transition cursor-pointer" @click="selectedTarif = 'classique'" :class="selectedTarif === 'classique' ? 'border-[#3f44d6] bg-gray-50' : ''">
            <h3 class="font-bold text-lg mb-2">Classique</h3>
            <p>35€/mois - Site + Page Google + Réservations</p>
          </div>
        </div>
      </div>

      <!-- Étape 5 : Récapitulatif -->
      <div v-if="currentStep === 4" class="max-w-2xl mx-auto">
        <h2 class="text-2xl font-bold mb-6">Récapitulatif de votre inscription</h2>
        <div class="bg-gray-50 rounded-lg p-6 space-y-4 border">
          <div>
            <h3 class="font-semibold">Organisation :</h3>
            <p>{{ selectedOption === 'solo' ? 'Je suis seul(e)' : 'J’ai une équipe' }}</p>
          </div>
          <div>
            <h3 class="font-semibold">Informations personnelles :</h3>
            <p>Nom : {{ nom }}</p>
            <p>Prénom : {{ prenom }}</p>
            <p>Téléphone : {{ telephone }}</p>
            <p>Email : {{ email }}</p>
          </div>
          <div>
            <h3 class="font-semibold">Entreprise :</h3>
            <p>Nom : {{ entreprise }}</p>
            <p>Téléphone : {{ telEntreprise }}</p>
            <p>SIRET : {{ siret }}</p>
            <p>Adresse : {{ adresse }}</p>
          </div>
          <div>
            <h3 class="font-semibold">Formule choisie :</h3>
            <p v-if="selectedTarif === 'flexible'">Flexible - {{ montantFlexible }}€/mois</p>
            <p v-else>Classique - 35€/mois</p>
          </div>
        </div>
      </div>

      <!-- Boutons navigation -->
      <div class="flex justify-between mt-12 max-w-3xl gap-4 mx-auto">
        <button class="px-4 py-2 border rounded-xl" @click="prevStep" :disabled="currentStep === 0">Précédent</button>
        <button class="px-4 py-2 bg-[#3f44d6] text-white rounded-xl" @click="nextStep">Continuer</button>
      </div>
    </div>
      </form>


    </div>
    <!-- Right side -->
    <div class="w-full md:w-1/2 flex justify-center items-center">
      <img alt="Abstract beige art with black strokes, smooth and glossy texture" class="object-cover w-full h-full" height="800" src="https://img.freepik.com/free-photo/bride-getting-makeup-done-medium-shot_23-2149860778.jpg?uid=R141327891&ga=GA1.1.223045777.1725963888&semt=ais_hybrid&w=740" width="600"/>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      isLoginForm: true,
      isResetPasswordForm: false,
      loginData: {
        email: '',
        password: ''
      },
      resetPasswordData: {
        email: ''
      },
      registerData: {
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        date_naissance: '',
        genre: '',
        password: '',
        passwordConfirm: '',
        acceptCGU: false
      },
      passwordFieldType: 'password'
    };
  },
  computed: {
    passwordIconClass() {
      return this.passwordFieldType === 'password' ? 'fas fa-eye-slash' : 'fas fa-eye';
    }
  },
  methods: {
    togglePasswordVisibility() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    },
    showRegisterForm() {
      this.isLoginForm = false;
      this.isResetPasswordForm = false;
    },
    showLoginForm() {
      this.isLoginForm = true;
      this.isResetPasswordForm = false;
    },
    showResetPasswordForm() {
      this.isLoginForm = false;
      this.isResetPasswordForm = true;
    },
    handleLogin() {
      console.log('Login data:', this.loginData);

      fetch('https://bookmysalon-967a856b16b6.herokuapp.com/api/clients/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(this.loginData)
      })
        .then(async response => {
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Erreur lors de la connexion.');
          }

          // Stocker le token dans le localStorage
          localStorage.setItem('clientToken', data.token);

          console.log('Connexion réussie, token enregistré :', data.token);
          alert('Connexion réussie !');
          this.$router.push('/rendez-vous');
          // Redirige ou mets à jour l’état ici si besoin
        })
        .catch(error => {
          console.error('Erreur de connexion :', error);
          alert(error.message);
        });
    },
    handleRegister() {
      if (this.registerData.password !== this.registerData.passwordConfirm) {
        alert("Les mots de passe ne correspondent pas.");
        return;
      }

      // Cloner et nettoyer les données à envoyer (retirer passwordConfirm)
      const payload = { ...this.registerData };
      delete payload.passwordConfirm;

      fetch('https://bookmysalon-967a856b16b6.herokuapp.com/api/clients/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
        .then(async response => {
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Erreur lors de l\'enregistrement.');
          }
          return response.json();
        })
        .then(data => {
          alert('Compte créé avec succès !');
          console.log('Réponse serveur :', data);
          // Réinitialiser les champs si nécessaire
          this.registerData = {
            nom: '',
            prenom: '',
            email: '',
            telephone: '',
            adresse: '',
            date_naissance: '',
            genre: '',
            password: '',
            passwordConfirm: '',
            acceptCGU: false
          };
          this.showLoginForm(); // Revenir à la page de connexion
        })
        .catch(error => {
          console.error('Erreur :', error);
          alert(error.message);
        });
    },
    handleResetPassword() {
      console.log('Reset password data:', this.resetPasswordData);

      // Envoyer l'email au backend pour la réinitialisation
      fetch('https://bookmysalon-967a856b16b6.herokuapp.com/api/clients/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(this.resetPasswordData)
      })
      .then(async response => {
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Erreur lors de la demande de réinitialisation.');
        }

        alert('Un email de réinitialisation a été envoyé à votre adresse.');
        this.showLoginForm(); // Revenir à la page de connexion
      })
      .catch(error => {
        console.error('Erreur de réinitialisation :', error);
        alert(error.message);
      });
    }
  }
};
</script>



<script setup>
import { ref } from 'vue';

const currentStep = ref(0);
const steps = ['Organisation', 'Infos Perso', 'Entreprise', 'Abonnement', 'Récapitulatif'];
const selectedOption = ref(null);
const selectedTarif = ref(null);
const nom = ref('');
const prenom = ref('');
const telephone = ref('');
const email = ref('');
const password = ref('');
const entreprise = ref('');
const telEntreprise = ref('');
const siret = ref('');
const adresse = ref('');
const montantFlexible = ref('');

function nextStep() {
  if (currentStep.value < steps.length - 1) currentStep.value++;
}

function prevStep() {
  if (currentStep.value > 0) currentStep.value--;
}

const baseBtnClass = 'flex flex-col items-center justify-center p-6 border rounded-lg hover:shadow transition';
const activeBtnClass = 'border-[#3f44d6] text-[#3f44d6]';
</script>

<style scoped>
.input {
  @apply border border-gray-300 rounded px-4 py-2 w-full;
}
</style>